import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import AuthCard from "@/components/gateway/AuthCard";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Eye, EyeOff, Loader2, Camera } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import LandingLegalFooter from "@/components/gateway/LandingLegalFooter";

const YELLOW = "#FFD028";
const RUST = "#C05621";
const HARD = "shadow-[4px_4px_0px_#1A1A1A]";
const PRESS =
  "transition-[transform,box-shadow] duration-150 active:translate-x-[3px] active:translate-y-[3px] active:shadow-none";

/* ------------------------------- shared bits ------------------------------ */
function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#C05621] mb-1.5">
        {label}
      </span>
      <input
        data-testid={testid}
        className={`w-full bg-white border-2 border-[#1A1A1A] ${HARD} rounded-xl px-3.5 py-3 font-mono text-sm text-[#1A1A1A] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#FFD028]`}
        {...props}
      />
    </label>
  );
}

function YellowButton({ children, testid, onClick, disabled, busy }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled || busy}
      className={`w-full flex items-center justify-center gap-2 bg-[#FFD028] text-[#1A1A1A] border-2 border-[#1A1A1A] ${HARD} ${PRESS} rounded-xl py-3.5 font-black text-sm uppercase tracking-[0.12em] disabled:opacity-60`}
    >
      {busy ? <Loader2 size={18} className="animate-spin" /> : children}
    </button>
  );
}

/* ---------------- Landing Page #1 video showcase (C1) ----------------- */
/* --------------------------------- LANDING -------------------------------- */
function Landing({ go }) {
  return (
    <div
      className="flex-1 flex flex-col justify-between p-8 md:p-10 overflow-y-auto no-scrollbar"
      style={{ fontFamily: "'Outfit', sans-serif" }}
      data-testid="landing-view"
    >
      {/* Top — friendly brand mark */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-2.5">
          <img
            src="/brand/misteck_icon.png"
            alt="Misteck"
            data-testid="landing-logo"
            className="h-9 w-9"
          />
          <span className="text-white text-xl font-semibold tracking-tight lowercase">misteck</span>
        </div>
        <button
          data-testid="nav-buy-a-song-link"
          onClick={() => window.location.assign("/music/buyasong")}
          className="rounded-full border border-white/15 px-4 py-2 text-[13px] font-medium text-white/85 transition-colors duration-200 hover:bg-white/5 hover:text-white"
        >
          Buy a Song
        </button>
      </motion.div>

      {/* Middle — a big, warm welcome with lots of breathing room */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
        className="py-10"
      >
        <h1 className="text-white font-semibold tracking-tight leading-[1.06] text-[2.5rem] sm:text-5xl">
          Make it.
          <br />
          Share it.
          <br />
          <span className="text-[#FFD028]">Get paid.</span>
        </h1>
        <p className="mt-5 max-w-[300px] text-[15px] leading-relaxed text-white/55">
          Your videos, music and images — all in one simple app. Free to start, made for everyone.
        </p>
        <button
          data-testid="hero-buy-a-song-btn"
          onClick={() => window.location.assign("/music/buyasong")}
          className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#FFD028] px-6 h-12 text-[#141414] font-semibold text-[15px] transition-transform duration-200 hover:scale-[1.015] active:scale-[0.98]"
        >
          Buy a Song <ArrowRight size={17} strokeWidth={2.5} />
        </button>
      </motion.div>

      {/* Bottom — two clear choices, nothing else competing */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
        className="space-y-3"
      >
        <button
          data-testid="join-now-btn"
          onClick={() => go("SIGNUP")}
          className="w-full h-14 rounded-full bg-[#FFD028] text-[#141414] font-semibold text-base flex items-center justify-center gap-2 transition-transform duration-200 hover:scale-[1.015] active:scale-[0.98]"
        >
          Join now — it's free <ArrowRight size={18} strokeWidth={2.5} />
        </button>
        <button
          data-testid="sign-in-btn"
          onClick={() => go("SIGNIN")}
          className="w-full h-14 rounded-full bg-transparent text-white/90 border border-white/15 font-medium text-base flex items-center justify-center transition-colors duration-200 hover:bg-white/5"
        >
          I already have an account
        </button>
        <button
          data-testid="landing-advertise-link"
          onClick={() => window.location.assign("/advertise")}
          className="w-full text-center text-[12px] text-white/35 hover:text-white/70 transition-colors pt-1.5"
        >
          Advertise with us
        </button>
        <LandingLegalFooter />
      </motion.div>
    </div>
  );
}

/* ----------------------------- CONFIRMATION CODE -------------------------- */
function ConfirmationCode({ go, pending, onVerified }) {
  const { verifyCode } = useSession();
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const refs = useRef([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const handle = (i, v) => {
    const c = v.replace(/\D/g, "").slice(-1);
    setDigits((d) => {
      const next = [...d];
      next[i] = c;
      return next;
    });
    if (c && i < 5) refs.current[i + 1]?.focus();
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await verifyCode(pending.email, code);
      onVerified();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-4 overflow-y-auto no-scrollbar">
      <button
        data-testid="code-back-btn"
        onClick={() => go("SIGNUP")}
        className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-neutral-400 hover:text-[#FFD028] mb-6 self-start"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFD028]">Resend Handshake</p>
        <h1 className="font-serif text-4xl text-white leading-none mt-1">Confirm Email</h1>
        <p className="font-mono text-[11px] text-neutral-400 mt-2">
          Enter the 6-digit code sent to <span className="text-white">{pending.email}</span>
        </p>
      </div>

      <form onSubmit={submit}>
        <div className="flex justify-between gap-2 mb-4" data-testid="code-inputs">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              data-testid={`code-digit-${i}`}
              value={d}
              onChange={(e) => handle(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              className={`w-full aspect-square bg-white border-2 border-[#1A1A1A] ${HARD} rounded-xl text-center font-mono text-2xl font-black text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#FFD028]`}
            />
          ))}
        </div>

        {pending.devCode && (
          <div
            className="mb-4 bg-[#111] border-2 border-dashed border-neutral-700 rounded-xl p-2.5 text-center"
            data-testid="dev-code-hint"
          >
            <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
              Simulated email · dev code
            </p>
            <p className="font-mono text-lg font-black text-[#FFD028] tracking-[0.4em]">{pending.devCode}</p>
          </div>
        )}

        <YellowButton testid="code-submit" busy={busy}>
          Verify Code <ArrowRight size={18} strokeWidth={2.6} />
        </YellowButton>
      </form>
    </div>
  );
}

/* ------------------------------ BIOMETRIC MODAL --------------------------- */
function BiometricModal() {
  const { setBiometric } = useSession();
  const [busy, setBusy] = useState(false);

  const choose = async (captured) => {
    setBusy(true);
    try {
      await setBiometric(captured);
      toast[captured ? "success" : "message"](
        captured ? "Identity verified ✅" : "Proceeding unverified — some features locked",
      );
    } catch (err) {
      toast.error("Could not save choice");
      setBusy(false);
    }
  };

  return (
    <div className="absolute inset-0 bg-black/85 z-50 flex items-center justify-center p-4" data-testid="biometric-modal">
      <div className={`w-[328px] max-w-[calc(100vw-32px)] bg-[#FDFBF7] border-4 border-[#1A1A1A] p-4 ${HARD} rounded-2xl text-neutral-900 space-y-4`}>
        <div className="text-center">
          <span className="inline-grid place-items-center w-12 h-12 rounded-xl border-2 border-[#1A1A1A] text-[#1A1A1A] bg-[#FFD028] shadow-[2px_2px_0px_#1A1A1A]">
            <Camera size={22} />
          </span>
          <h3 className="text-sm font-black uppercase mt-2 tracking-wide">Identity Capture Layer</h3>
          <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest mt-1">
            Take a quick camera scan to secure your profile signature
          </p>
        </div>
        <div className="w-full h-32 bg-neutral-900 rounded-xl border-2 border-[#1A1A1A] flex items-center justify-center">
          <span className="text-[9px] font-mono text-neutral-400 animate-pulse">
            CONNECTING TO VIEWPORT INFRASTRUCTURE…
          </span>
        </div>
        <div className="space-y-2">
          <button
            data-testid="biometric-verify-btn"
            onClick={() => choose(true)}
            disabled={busy}
            className={`w-full bg-[#1A1A1A] text-white text-xs font-black py-3 rounded-xl uppercase tracking-wider shadow-[3px_3px_0px_#FFD028] ${PRESS} disabled:opacity-60`}
          >
            {busy ? "…" : "Verify via Camera Photo"}
          </button>
          <button
            data-testid="biometric-optout-btn"
            onClick={() => choose(false)}
            disabled={busy}
            className="w-full bg-neutral-100 text-neutral-500 border-2 border-neutral-300 text-[10px] font-bold py-2.5 rounded-xl uppercase tracking-wider"
          >
            Opt Out &amp; Proceed Unverified →
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- SHELL ---------------------------------- */
/* ----------------------------- FORGOT PASSWORD --------------------------- */
function ForgotPassword({ go }) {
  const { forgotPassword, resetPassword } = useSession();
  const [step, setStep] = useState("REQUEST"); // REQUEST | RESET
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      const data = await forgotPassword(email);
      setDevCode(data.dev_code || "");
      setStep("RESET");
      toast.success("If that email exists, a reset code was sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset code");
    } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await resetPassword(email, code, pw);
      toast.success("Password updated — sign in with your new password");
      go("SIGNIN");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 flex flex-col p-4 overflow-y-auto no-scrollbar" data-testid="forgot-password-view">
      <button
        data-testid="forgot-back-btn"
        onClick={() => go("SIGNIN")}
        className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-neutral-400 hover:text-[#FFD028] mb-6 self-start"
      >
        <ArrowLeft size={14} /> Back to sign in
      </button>

      <div className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFD028]">Account Recovery</p>
        <h1 className="font-serif text-4xl text-white leading-none mt-1">Reset Password</h1>
        <p className="font-mono text-[11px] text-neutral-400 mt-2">
          {step === "REQUEST"
            ? "Enter your account email and we'll send a 6-digit reset code."
            : <>Enter the code sent to <span className="text-white">{email}</span> and choose a new password.</>}
        </p>
      </div>

      {step === "REQUEST" ? (
        <form onSubmit={request} className="space-y-4">
          <Field label="Email" testid="forgot-email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" />
          <YellowButton testid="forgot-request-submit" busy={busy}>
            Send Reset Code <ArrowRight size={18} strokeWidth={2.6} />
          </YellowButton>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-4">
          <Field label="6-Digit Code" testid="forgot-code" inputMode="numeric" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456" />
          <label className="block">
            <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#C05621] mb-1.5">New Password</span>
            <div className="relative">
              <input
                data-testid="forgot-new-password"
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="min 6 chars"
                className={`w-full bg-white border-2 border-[#1A1A1A] ${HARD} rounded-xl px-3.5 py-3 pr-11 font-mono text-sm text-[#1A1A1A] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#FFD028]`}
              />
              <button type="button" data-testid="forgot-toggle-password" onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>
          {devCode && (
            <div className="bg-[#111] border-2 border-dashed border-neutral-700 rounded-xl p-2.5 text-center" data-testid="forgot-dev-code-hint">
              <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Simulated email · dev code</p>
              <p className="font-mono text-lg font-black text-[#FFD028] tracking-[0.4em]">{devCode}</p>
            </div>
          )}
          <YellowButton testid="forgot-reset-submit" busy={busy}>
            Update Password <ArrowRight size={18} strokeWidth={2.6} />
          </YellowButton>
        </form>
      )}
    </div>
  );
}

export default function Gateway() {
  const [view, setView] = useState("LANDING");
  const [pending, setPending] = useState({ email: "", devCode: "" });
  const [biometric, setBiometric] = useState(false);

  const renderView = () => (
    <>
      {view === "LANDING" && <Landing go={setView} />}
      {view === "SIGNIN" && <AuthCard go={setView} onPending={(email, devCode) => setPending({ email, devCode })} defaultTab="signin" />}
      {view === "FORGOT" && <ForgotPassword go={setView} />}
      {view === "SIGNUP" && (
        <AuthCard go={setView} onPending={(email, devCode) => setPending({ email, devCode })} defaultTab="signup" />
      )}
      {view === "CODE" && (
        <ConfirmationCode go={setView} pending={pending} onVerified={() => setBiometric(true)} />
      )}
    </>
  );

  return (
    <div className="relative w-full h-full overflow-hidden select-none bg-[#0A0A0A] md:flex" data-testid="gateway-root">
      {biometric && <BiometricModal />}

      {/* The mobile app wrapper: full column on phones; docks to a fixed-width
          panel on the LEFT on desktop. Rendered ONCE (no duplicate nodes). */}
      <div className="w-full h-full flex flex-col justify-between md:w-[460px] md:shrink-0 md:border-r md:border-white/10 md:overflow-y-auto md:no-scrollbar"
        data-testid="gateway-left-wrapper">
        {renderView()}
      </div>

      {/* Calm brand aside on desktop — the new glyph + wordmark, lots of quiet space */}
      <div className="hidden md:flex flex-1 h-full relative overflow-hidden flex-col items-center justify-center bg-[#0A0A0A]"
        data-testid="gateway-aside" style={{ fontFamily: "'Outfit', sans-serif" }}>
        <motion.img
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          src="/brand/misteck_icon.png" alt="Misteck"
          data-testid="gateway-logo-mark"
          className="w-44 h-44 lg:w-52 lg:h-52 drop-shadow-[0_16px_60px_rgba(255,208,40,0.28)]" />
        <motion.span
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.35 }}
          className="mt-7 text-white text-4xl lg:text-5xl font-semibold tracking-tight lowercase">
          misteck
        </motion.span>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.6 }}
          className="mt-3 text-[13px] tracking-wide text-white/40">
          The all-in-one creative app · free to start
        </motion.p>
      </div>
    </div>
  );
}
